import React, { useState, useEffect, useCallback } from "react";
import { Drawer, Button, Form, Input, InputNumber, Select, Switch, Space, Tag, Typography, Tooltip, Statistic, Badge, Divider, Alert, Modal, Row, Col } from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined, EditOutlined, DeleteOutlined, ShoppingOutlined, BoxPlotOutlined, SaveOutlined, SyncOutlined } from "@ant-design/icons";
import { fetchProductVariants, createProductVariant, updateProductVariant, deleteProductVariant } from "../../../../../api/inventory/productVariantApi";
import { useNotifications } from "../../../../../context/NotificationContext";
import { SectionCard, DataTable } from "../../../../../components/shared";
import type { Product, ProductVariant } from "../../../../../types/inventory/ProductTypes";

const { Title, Text } = Typography;

interface VariantsManagementTabProps {
	product: Product;
	/**
	 * Called after any mutation with the fresh variants array so the parent
	 * can keep the badge count and its own product state in sync.
	 */
	onVariantUpdate?: (variants: ProductVariant[]) => void;
}

const VariantsManagementTab: React.FC<VariantsManagementTabProps> = ({ product, onVariantUpdate }) => {
	const [variants, setVariants] = useState<ProductVariant[]>(product.variants ?? []);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [editingVariant, setEditingVariant] = useState<ProductVariant | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [loading, setLoading] = useState(false);
	const [form] = Form.useForm();
	const { showError, showSuccess } = useNotifications();

	// ── Data ────────────────────────────────────────────────────────────────

	const reload = useCallback(async () => {
		if (!product.product_id) return;
		setLoading(true);
		try {
			const fresh = await fetchProductVariants(product.product_id);
			setVariants(fresh);
			onVariantUpdate?.(fresh);
		} catch {
			showError("Failed to refresh variants");
		} finally {
			setLoading(false);
		}
	}, [product.product_id]);

	useEffect(() => {
		reload();
	}, [reload]);

	// ── Drawer open helpers ──────────────────────────────────────────────────

	const openAdd = () => {
		setEditingVariant(null);
		form.resetFields();
		form.setFieldsValue({
			inventory_management: "shopify",
			inventory_policy: "deny",
			weight_unit: "kg",
			is_active: true,
		});
		setDrawerOpen(true);
	};

	const openEdit = (v: ProductVariant) => {
		setEditingVariant(v);
		form.setFieldsValue({
			title: v.title,
			sku: v.sku,
			barcode: v.barcode,
			price: v.price,
			compare_at_price: v.compare_at_price,
			cost: v.cost,
			option1: v.option1,
			option2: v.option2,
			option3: v.option3,
			weight: v.weight,
			weight_unit: v.weight_unit ?? "kg",
			inventory_management: v.inventory_management ?? "shopify",
			inventory_policy: v.inventory_policy ?? "deny",
			is_active: v.is_active,
		});
		setDrawerOpen(true);
	};

	// ── CRUD ─────────────────────────────────────────────────────────────────

	const handleSubmit = async () => {
		let values: Record<string, any>;
		try {
			values = await form.validateFields();
		} catch {
			return; // Ant Design shows inline errors
		}

		setSubmitting(true);
		try {
			if (editingVariant) {
				await updateProductVariant(editingVariant.id, values);
				showSuccess("Variant updated" + (product.product_shopify_id ? " and synced to Shopify" : ""));
			} else {
				await createProductVariant({ ...values, product_id: product.product_id } as any);
				showSuccess("Variant created" + (product.product_shopify_id ? " and synced to Shopify" : ""));
			}
			setDrawerOpen(false);
			await reload();
		} catch (err: any) {
			const msg = err?.response?.data?.error ?? "Failed to save variant";
			showError(msg);
		} finally {
			setSubmitting(false);
		}
	};

	const confirmDelete = (v: ProductVariant) => {
		const activeVariants = variants.filter((x) => x.is_active);

		// Only one active variant left — cannot delete it
		if (activeVariants.length <= 1) {
			if (v.is_default_variant) {
				Modal.info({
					title: "Default Variant",
					content: "This is the auto-generated default variant. Add a custom variant (with options like Size or Colour) and the default will be replaced automatically.",
				});
			} else {
				Modal.error({
					title: "Cannot Delete Last Variant",
					content: "A product must have at least one active variant. Delete the product instead if you want to remove it entirely.",
				});
			}
			return;
		}

		Modal.confirm({
			title: v.is_default_variant ? "Remove Default Variant" : "Delete Variant",
			width: 460,
			content: (
				<Space direction="vertical" size="small" style={{ width: "100%" }}>
					<Text>
						{v.is_default_variant ? (
							'Remove the "Default Title" placeholder variant? Your other variants will remain.'
						) : (
							<>
								Delete <Text strong>{v.formatted_title ?? v.title ?? "this variant"}</Text>? This cannot be undone.
							</>
						)}
					</Text>
					{v.shopify_variant_id && <Alert message="This variant will also be removed from Shopify" type="warning" showIcon />}
					{!v.is_default_variant && (
						<Text type="secondary" style={{ fontSize: 12 }}>
							Stock and order history are preserved. The variant is deactivated, not destroyed.
						</Text>
					)}
				</Space>
			),
			okText: v.is_default_variant ? "Remove" : "Delete",
			okType: "danger",
			onOk: async () => {
				try {
					await deleteProductVariant(v.id);
					showSuccess(v.is_default_variant ? "Default variant removed" : "Variant deleted" + (v.shopify_variant_id ? " and removed from Shopify" : ""));
					if (drawerOpen && editingVariant?.id === v.id) setDrawerOpen(false);
					await reload();
				} catch (err: any) {
					showError(err?.response?.data?.error ?? "Failed to delete variant");
				}
			},
		});
	};

	// ── Stock display helpers ────────────────────────────────────────────────

	const getAvailable = (v: ProductVariant) => v.total_stock ?? v.stock?.reduce((s, e) => s + (e.quantity ?? 0), 0) ?? 0;

	const getCommitted = (v: ProductVariant) => v.committed_stock ?? v.stock?.reduce((s, e) => s + ((e as any).committed_quantity ?? 0), 0) ?? 0;

	const getOnHand = (v: ProductVariant) => v.on_hand_stock ?? getAvailable(v) + getCommitted(v);

	const stockBadge = (available: number) => {
		if (available === 0) return <Badge status="error" text="Out of stock" />;
		if (available < 10) return <Badge status="warning" text={`${available} avail.`} />;
		return <Badge status="success" text={`${available} avail.`} />;
	};

	// ── Aggregate stats ──────────────────────────────────────────────────────

	const active = variants.filter((v) => v.is_active);
	const totalAvailable = active.reduce((s, v) => s + getAvailable(v), 0);
	const totalCommitted = active.reduce((s, v) => s + getCommitted(v), 0);
	const totalOnHand = active.reduce((s, v) => s + getOnHand(v), 0);

	// ── Table columns ────────────────────────────────────────────────────────

	const columns: ColumnsType<ProductVariant> = [
		{
			title: "Variant",
			key: "variant",
			render: (_, r) => (
				<Space direction="vertical" size={0}>
					<Space>
						<Text strong>{r.formatted_title ?? r.title ?? "Default"}</Text>
						{!r.is_active && <Tag color="default">Inactive</Tag>}
						{r.is_default_variant && <Tag color="blue">Default</Tag>}
						{r.shopify_variant_id && (
							<Tag icon={<ShoppingOutlined />} color="success">
								Shopify
							</Tag>
						)}
					</Space>
					{r.sku && (
						<Text type="secondary" style={{ fontSize: 12 }}>
							SKU: {r.sku}
						</Text>
					)}
				</Space>
			),
		},
		{
			title: "Price",
			dataIndex: "price",
			key: "price",
			width: 90,
			align: "right",
			render: (p: number) => (
				<Text strong style={{ color: "var(--mg-success)" }}>
					${Number(p ?? 0).toFixed(2)}
				</Text>
			),
		},
		{
			title: <Tooltip title="Units available to purchase (Shopify available)">Available</Tooltip>,
			key: "available",
			width: 110,
			align: "center",
			render: (_, r) => stockBadge(getAvailable(r)),
		},
		{
			title: <Tooltip title="Ordered but not yet shipped (Shopify committed)">Committed</Tooltip>,
			key: "committed",
			width: 110,
			align: "center",
			render: (_, r) => {
				const c = getCommitted(r);
				return c > 0 ? <Badge status="warning" text={`${c}`} /> : <Text type="secondary">—</Text>;
			},
		},
		{
			title: <Tooltip title="Physical units = available + committed">On Hand</Tooltip>,
			key: "on_hand",
			width: 90,
			align: "center",
			render: (_, r) => (
				<Text strong style={{ color: "var(--mg-primary)" }}>
					{getOnHand(r)}
				</Text>
			),
		},
		{
			title: "Actions",
			key: "actions",
			width: 120,
			align: "center",
			render: (_, r) => (
				<Space size="small">
					<Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
						Edit
					</Button>
					<Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => confirmDelete(r)}>
						Delete
					</Button>
				</Space>
			),
		},
	];

	// ── Render ───────────────────────────────────────────────────────────────

	const isEditing = !!editingVariant;

	return (
		<div>
			<Space direction="vertical" style={{ width: "100%" }} size="large">
				{/* Summary stats */}
				<Row gutter={16}>
					<Col xs={12} md={6}>
						<SectionCard>
							<Statistic title="Total Variants" value={variants.length} prefix={<BoxPlotOutlined />} valueStyle={{ color: "var(--mg-primary)" }} />
						</SectionCard>
					</Col>
					<Col xs={12} md={6}>
						<SectionCard>
							<Statistic title={<Tooltip title="Purchasable (Shopify available)">Available</Tooltip>} value={totalAvailable} valueStyle={{ color: "var(--mg-success)" }} />
						</SectionCard>
					</Col>
					<Col xs={12} md={6}>
						<SectionCard>
							<Statistic title={<Tooltip title="Ordered, not shipped (Shopify committed)">Committed</Tooltip>} value={totalCommitted} valueStyle={{ color: "var(--mg-warning)" }} />
						</SectionCard>
					</Col>
					<Col xs={12} md={6}>
						<SectionCard>
							<Statistic title={<Tooltip title="Physical units = available + committed">On Hand</Tooltip>} value={totalOnHand} suffix="units" valueStyle={{ color: "var(--mg-info)" }} />
						</SectionCard>
					</Col>
				</Row>

				{/* Variants table */}
				<SectionCard
					title="Product Variants"
					extra={
						<Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
							Add Variant
						</Button>
					}
				>
					{product.product_shopify_id && (
						<Alert
							message={
								<Space>
									<SyncOutlined />
									Shopify connected — variants sync automatically
								</Space>
							}
							type="info"
							showIcon={false}
							style={{ marginBottom: 12 }}
						/>
					)}

					{variants.length === 1 && variants[0]?.is_default_variant && (
						<Alert
							message="Using default variant"
							description='This product has a single "Default Title" variant (no custom options). Add a variant with options like Size or Colour — the default will be replaced automatically.'
							type="info"
							showIcon
							style={{ marginBottom: 12 }}
						/>
					)}

					{variants.length === 0 && !loading ? null : (
						<DataTable
							columns={columns}
							dataSource={variants}
							rowKey="id"
							loading={loading}
							pagination={false}
							bare
							emptyTitle="No variants yet"
							emptyDescription="Add a variant to begin tracking stock"
							onRowClick={openEdit}
						/>
					)}
				</SectionCard>
			</Space>

			{/* ── Add / Edit Drawer ─────────────────────────────────────────── */}
			<Drawer
				title={isEditing ? `Edit: ${editingVariant.formatted_title ?? editingVariant.title ?? "Variant"}` : "Add Variant"}
				placement="right"
				open={drawerOpen}
				onClose={() => setDrawerOpen(false)}
				width={520}
				extra={
					<Space>
						{isEditing && variants.filter((x) => x.is_active).length > 1 && (
							<Button
								danger
								icon={<DeleteOutlined />}
								onClick={() => {
									setDrawerOpen(false);
									confirmDelete(editingVariant!);
								}}
							>
								{editingVariant?.is_default_variant ? "Remove" : "Delete"}
							</Button>
						)}
						<Button type="primary" icon={<SaveOutlined />} loading={submitting} onClick={handleSubmit}>
							{isEditing ? "Save Changes" : "Create Variant"}
						</Button>
					</Space>
				}
			>
				{product.product_shopify_id && <Alert message="Changes will be synced to Shopify automatically" type="info" showIcon style={{ marginBottom: 20 }} />}

				{isEditing && editingVariant?.is_default_variant && (
					<Alert
						message="Default variant"
						description='This is the auto-generated "Default Title" placeholder. You can update the price and SKU here. To add custom options (Size, Colour, etc.) close this and click "Add Variant" — the default will be replaced automatically.'
						type="info"
						showIcon
						style={{ marginBottom: 20 }}
					/>
				)}

				<Form form={form} layout="vertical">
					{/* ── Details ─── */}
					<Title level={5}>Variant Details</Title>
					<Row gutter={16}>
						<Col span={24}>
							<Form.Item label="Title" name="title" tooltip="Display name. Leave blank to auto-generate from options.">
								<Input placeholder="e.g. Small / Red" />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="SKU" name="sku" rules={[{ required: true, message: "SKU is required" }]}>
								<Input placeholder="Unique identifier" />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="Barcode" name="barcode">
								<Input placeholder="EAN, UPC, ISBN…" />
							</Form.Item>
						</Col>
					</Row>

					<Divider />

					{/* ── Pricing ─── */}
					<Title level={5}>Pricing</Title>
					<Row gutter={16}>
						<Col span={12}>
							<Form.Item label="Price" name="price" rules={[{ required: true, message: "Price is required" }]}>
								<InputNumber style={{ width: "100%" }} prefix="$" precision={2} min={0} placeholder="0.00" />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="Compare at Price" name="compare_at_price">
								<InputNumber style={{ width: "100%" }} prefix="$" precision={2} min={0} placeholder="0.00" />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="Cost per Item" name="cost">
								<InputNumber style={{ width: "100%" }} prefix="$" precision={2} min={0} placeholder="0.00" />
							</Form.Item>
						</Col>
					</Row>

					<Divider />

					{/* ── Shipping ─── */}
					<Title level={5}>Shipping</Title>
					<Row gutter={16}>
						<Col span={14}>
							<Form.Item label="Weight" name="weight">
								<InputNumber style={{ width: "100%" }} precision={2} min={0} placeholder="0.00" />
							</Form.Item>
						</Col>
						<Col span={10}>
							<Form.Item label="Unit" name="weight_unit">
								<Select>
									<Select.Option value="kg">kg</Select.Option>
									<Select.Option value="g">g</Select.Option>
									<Select.Option value="lb">lb</Select.Option>
									<Select.Option value="oz">oz</Select.Option>
								</Select>
							</Form.Item>
						</Col>
					</Row>

					<Divider />

					{/* ── Inventory ─── */}
					<Title level={5}>Inventory</Title>
					<Row gutter={16}>
						<Col span={12}>
							<Form.Item label="Inventory Management" name="inventory_management">
								<Select>
									<Select.Option value="shopify">Shopify</Select.Option>
									<Select.Option value="">Not managed</Select.Option>
								</Select>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="When out of stock" name="inventory_policy">
								<Select>
									<Select.Option value="deny">Deny orders</Select.Option>
									<Select.Option value="continue">Allow orders</Select.Option>
								</Select>
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item label="Active" name="is_active" valuePropName="checked">
								<Switch />
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</Drawer>
		</div>
	);
};

export default VariantsManagementTab;
